import React, { useEffect } from "react";
import { onMessage, MessagePayload } from "firebase/messaging";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { getMessagingInstance } from "../services/firebase.service";
import PushNotificationToast from "../components/Generic/Toast/PushNotificationToast";

interface NotificationHandlerOptions {
  enabled?: boolean;
  onNotificationClick?: (data?: any) => void;
  showToast?: boolean;
}

export const useNotificationHandler = (options: NotificationHandlerOptions = {}) => {
  const { enabled = true, onNotificationClick, showToast = true } = options;
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!enabled) return;

    const messaging = getMessagingInstance();
    if (!messaging) {
      console.warn("Firebase Messaging not initialized");
      return;
    }

    const unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
      console.log("Message received (foreground):", payload);

      const notificationTitle = payload.notification?.title || translate("newNotification") || "";
      const notificationBody = payload.notification?.body || "";

      if (showToast) {
        toast.custom(
          (t) => (
            <PushNotificationToast
              title={notificationTitle}
              content={notificationBody}
              appName="Shipeezi"
              onClose={() => toast.dismiss(t.id)}
            />
          ),
          {
            duration: 5000,
            position: "bottom-right",
          }
        );
        
        if (onNotificationClick && payload.data) {
          // onNotificationClick(payload.data);
        }
      }

      if (Notification.permission === "granted" && document.hidden) {
        try {
          const notification = new Notification(notificationTitle, {
            body: notificationBody,
            icon: payload.notification?.icon || "/favicon.ico",
            badge: "/favicon.ico",
            data: payload.data,
            tag: payload.data?.tag || "default",
          });

          notification.onclick = () => {
            window.focus();
            if (onNotificationClick && payload.data) {
              onNotificationClick(payload.data);
            }
            notification.close();
          };
        } catch (err) {
          console.error("Error showing browser notification:", err);
        }
      }
    });

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "NOTIFICATION_CLICKED") {
        const url = event.data.url;
        if (url && url !== "/") {
          window.location.href = url;
        }
        if (onNotificationClick && event.data) {
          onNotificationClick(event.data);
        }
      }
    };

    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);

    return () => {
      unsubscribe();
      navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, [enabled, onNotificationClick, showToast]);
};

export default useNotificationHandler;
